import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

//Common
import { LoadingIndicator } from "../common/VFX-UI";
import {
    getOrganizationId,
    IsOrganizationUser,
    IsCauwUser
} from "../../utility/helpers/claimHelper";

//App
import organizationApi from "../../app/model/api/organizationApi";
import { queryKeys } from "./constants";
import { EMPTY_GUID, GRID_PAGE_SIZE } from "../../utility/constants/constants";
import { SearchDefaultValue, SearchValue } from "../../store/types";
import { ContainerContentProps, SortBy } from "../../types";
import { CampaignContextData, CampaignUseState } from "../../types/campaign";
import {
    selectIsActive,
    selectSearchValue,
    setIsActive,
    setSearchValue,
} from "../../store/campaign";
import Campaigns from "./Campaigns";

export const CampaignContext = React.createContext<CampaignContextData | null>(
    null
);

const CampaignsContainer: React.FC<ContainerContentProps> = (props) => {
    const dispatch = useDispatch();
    const organizationId = IsOrganizationUser() ? getOrganizationId()
            : IsCauwUser() ? window.location.pathname.split("/")[2]
            : EMPTY_GUID;

    const [state, setState] = useState<CampaignUseState>({
        campaignId: null,
        activeToggle: useSelector(selectIsActive) ?? true,
        searchPayload: useSelector(selectSearchValue) ?? null,
        pageSize: GRID_PAGE_SIZE,
        currentPage: 1,
        sortBy: [{ field: "name", direction: 1 }],
    });

    const searchValue: SearchValue =
        useSelector(selectSearchValue) ?? SearchDefaultValue;

    const campaignGridQuery = useQuery({
        queryKey: [queryKeys.searchOrganizationCampaign],
        queryFn: () =>
            organizationApi.searchOrganizationCampaign({
                ...state.searchPayload,
                organizationId: organizationId,
                isActive: state.activeToggle,
                pageSize: state.pageSize,
                pageNumber: state.currentPage,
                sortBy: state.sortBy,
            }),
    });

    const { isLoading: isLookupsDataLoading, data: campaignLookupData } =
        useQuery({
            queryKey: [queryKeys.getOrganizationCampaignLookups],
            queryFn: () => organizationApi.getOrganizationCampaignLookups(),
        });

    const campaignQuery = useQuery({
        queryKey: [queryKeys.getOrganizationCampaign, state.campaignId],
        queryFn: () =>
            organizationApi.getOrganizationCampaign(state.campaignId ?? EMPTY_GUID),
        enabled: state.campaignId !== null,
    });

    const { mutate: deleteMutation } = useMutation<void, Error, string>({
        mutationFn: async (value: string) => {
            await organizationApi.deactivateOrganizationCampaign(value);
        },
        onError: () => console.log("error"),
        onSuccess: () => console.log("success"),
    });

    const { mutate: activateMutation } = useMutation<void, Error, string>({
        mutationFn: async (value: string) => {
            await organizationApi.activateOrganizationCampaign(value);
        },
        onError: () => console.log("error"),
        onSuccess: () => console.log("success"),
    });

    const sendEmployeeMail = async () => {
        let result = false;
        try {
            if (state?.campaignId) {
                const response = await organizationApi.sendEmployeeMail(
                    state.campaignId
                );
                if (response.isSuccess) result = true;
            }
        } catch { }
        return result;
    };

    const updateCampaignId = async (campaignId: string | null) => {
        setState((prevState) => ({
            ...prevState,
            campaignId: campaignId,
        }));
    };

    const updateActiveToggle = async () => {
        dispatch(setIsActive());
        setState((prevState) => ({
            ...prevState,
            activeToggle: !prevState.activeToggle,
        }));
    };

    const reloadCampaignGrid = async () => {
        await setState((prevState) => ({
            ...prevState,
            searchPayload: null,
            currentPage: 1,
            sortBy: [{ field: "name", direction: 1 }],
        }));
        campaignGridQuery.refetch();
    };

    const updateSearchPayload = async (values: SearchValue | null) => {
        if (values?.name) {
            dispatch(setSearchValue({ name: values.name }));
        }
        if (values && Object.keys(values).length) {
            setState((prevState) => ({
                ...prevState,
                searchPayload: values,
                currentPage: 1,
            }));
        } else {
            setState((prevState) => ({
                ...prevState,
                searchPayload: null,
                currentPage: 1,
                sortBy: [{ field: "name", direction: 1 }],
            }));
        }
    };

    const updateCurrentPage = async (currentPage: number) => {
        setState((prevState) => ({
            ...prevState,
            currentPage: currentPage,
        }));
    };

    const updateCurrentPageSize = async (pageSize: number) => {
        setState((prevState) => ({
            ...prevState,
            pageSize: pageSize,
        }));
    };

    const updateSortBy = async (sortBy: SortBy) => {
        const { field, direction } = sortBy;
        setState((prevState) => ({
            ...prevState,
            sortBy: [{ field, direction }],
        }));
    };

    const contextValue: CampaignContextData = {
        data: campaignQuery?.data?.result,
        organizations: campaignLookupData?.result?.organizations,
        id: state.campaignId,
        pageSize: state.pageSize,
        activeToggle: state.activeToggle,
        setId: updateCampaignId,
        refetchGrid: () => campaignGridQuery.refetch(),
        setActiveToggleValue: updateActiveToggle,
        setSearchValue: updateSearchPayload,
        setCurrentPage: updateCurrentPage,
        setSortBy: updateSortBy,
        setCurrentPageSize: updateCurrentPageSize,
        paging: {
            currentPage: state.currentPage,
            totalSize: campaignGridQuery.data?.totalSize ?? 0,
        },
    };

    return campaignGridQuery.isLoading ? (
        <LoadingIndicator />
    ) : (
        <CampaignContext.Provider value={contextValue}>
            <Campaigns
                fullscreen={props.fullscreen}
                data={campaignGridQuery?.data?.result}
                deleteMutation={deleteMutation}
                activateMutation={activateMutation}
                sendEmployeeMail={sendEmployeeMail}
            />
        </CampaignContext.Provider>
    );
};

export default CampaignsContainer;
